import {
  CategoryOutlined,
  DnsOutlined,
  ExpandMoreOutlined,
  OpenInFullOutlined,
  PublicOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  createSvgIcon,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ReactComponent as IconEasyFlow } from "../../../../assets/icons/IconEasyFlow.svg";
import TrafficChartCard from "../../../../components/chartComponents/trafficChartCard";
import DatagridComponent from "../../../../components/datagridComponent";
import GrupoChip from "../../../../components/grupoEconomico/GrupoChip";
import LineChartComponent from "../../../../components/lineComponent";
import ModalChartHistory from "../../../../components/modalChartHistory";
import useStyles from "./styles";
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
const isSameOrAfter = require("dayjs/plugin/isSameOrAfter");
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
const timeZone = "America/Sao_Paulo";

// Tipo: 0: ASN, 1: CDNs, 2: Tráfego (mesmo mapeamento do schema.prisma)
const TIPO_CDN = 1;
const TIPO_CLASSE = 2;

// Mapeia a cor semântica cadastrada no catálogo (administracao/configFlow)
// para o valor aceito pelo ModalChartHistory ("blue"/"green"/"red"/"orange")
const MODAL_COLOR_POR_COR = {
  azul: "blue",
  verde: "green",
  vermelho: "red",
  laranja: "orange",
};

// Espelha pages/internal/3.0-dashboard/3.8-flow (mesmos cards/accordions/zoom,
// catálogo dinâmico via ItensFlow/ConfigItensFlowCliente). Diferenças: usa
// apiCliente (via api do contexto do layout do cliente) e resolve o clienteId
// direto do usuário logado — o layout do cliente não popula filterData.clienteId
// nem tem UI de "grupos de blocos", então esses dois pontos não têm equivalente
// aqui.
export default function EasyFlow() {
  const topItens = 20;
  const theme = useTheme();
  const styles = useStyles(theme);

  const usuarioCliente = JSON.parse(localStorage.getItem("userCliente"));
  const clienteId = usuarioCliente?.usuarioCliente?.clienteId;

  const [rows, setRows] = useState([]);
  const [dataGraph, setDataGraph] = useState({ trafficAns: [] });
  const [valuePercentil, setValuePercentil] = useState({ trafficAns: 0 });
  const [loadingDashFlow, setLoadingDashFlow] = useState({
    trafficAns: false,
  });
  const [openAsnModal, setOpenAsnModal] = useState(false);
  // Catálogo de CDNs/classes (ItensFlow) e quais estão habilitados para o
  // cliente logado (ConfigItensFlowCliente) — são os únicos dados que
  // definem quais cards de CDN/Tráfego aparecem e o que é buscado na API.
  // ASN não passa por essa checagem e é sempre exibido.
  const [catalogoFlow, setCatalogoFlow] = useState([]);
  const [itensHabilitadosIds, setItensHabilitadosIds] = useState(null);
  const EasyFlowIcon = createSvgIcon(<IconEasyFlow />);

  const {
    filterData,
    handleAplicarZoom,
    setCounter,
    setLoadingCronometro,
    selectTime,
    optionsTime,
    startCronometro,
    controllerDashboard,
    setControllerDashboard,
    stopCronometro,
    setPropsLayout,
    api,
  } = useOutletContext();

  const colorGraph = {
    asn: {
      graph: theme.palette.cardGraphic.graphColor.green,
      title: theme.palette.cardGraphic.graphColor.green,
    },
  };

  // Mesma resolução de cor semântica (azul/verde/vermelho/laranja) usada em
  // administracao/configFlow e no dashboard interno
  const coresCatalogo = {
    azul:
      theme.palette.mode === "dark"
        ? theme.palette.cardGraphic.graphColor.blueMedium
        : theme.palette.cardGraphic.graphColor.blue,
    verde: theme.palette.cardGraphic.graphColor.green,
    vermelho: theme.palette.cardGraphic.graphColor.red,
    laranja: theme.palette.cardGraphic.graphColor.orangeMedium,
  };
  const corPadraoCatalogo = coresCatalogo.azul;

  // Itens do catálogo habilitados para o cliente logado — é o que
  // efetivamente controla quais cards de CDN/Tráfego são exibidos
  const itensFlowVisiveis = useMemo(() => {
    if (!itensHabilitadosIds) return [];
    return catalogoFlow.filter((item) => itensHabilitadosIds.has(item.id));
  }, [catalogoFlow, itensHabilitadosIds]);

  const cdnItensVisiveis = useMemo(
    () => itensFlowVisiveis.filter((item) => item.tipo === TIPO_CDN),
    [itensFlowVisiveis],
  );
  const classeItensVisiveis = useMemo(
    () => itensFlowVisiveis.filter((item) => item.tipo === TIPO_CLASSE),
    [itensFlowVisiveis],
  );

  // No máximo 3 cards por linha; havendo menos itens que colunas, eles se
  // esticam para ocupar todo o espaço horizontal. Calculado por seção
  // (CDNs/Classes), não pelo total combinado.
  const getColunasCards = (total) => ({
    xs: `repeat(${Math.min(1, total) || 1}, 1fr)`,
    sm: `repeat(${Math.min(2, total) || 1}, 1fr)`,
    md: `repeat(${Math.min(3, total) || 1}, 1fr)`,
  });

  // Nome do CDN/classe sempre em maiúsculas no título do card (ex.: "Consumo
  // para META"), mesmo que o identificador tenha sido salvo em minúsculas
  const getTituloCard = (item) =>
    item.tipo === TIPO_CDN
      ? `Consumo para ${item.identificador?.toUpperCase()}`
      : `Tráfego ${item.identificador?.toUpperCase()}`;

  const renderTrafficCard = (item) => (
    <TrafficChartCard
      key={item.id}
      title={getTituloCard(item)}
      icon={item.imagem || PublicOutlined}
      loading={loadingDashFlow?.[item.id]}
      color={{
        graph: coresCatalogo[item.cor] || corPadraoCatalogo,
        icon: coresCatalogo[item.cor] || corPadraoCatalogo,
      }}
      data={dataGraph?.[item.id]}
      percentil={valuePercentil?.[item.id]}
      handleAplicarZoom={handleAplicarZoom}
      modalColor={MODAL_COLOR_POR_COR[item.cor] || "blue"}
    />
  );

  // Soma o tráfego de TODOS os CDNs habilitados para o cliente de uma vez
  // (getTrafficCdnConsumo, no backend, combina a condição de cada CDN
  // permitido com OR). Fica dentro de "Divisão por Classes" e só aparece se
  // o cliente tiver ao menos um CDN habilitado.
  const renderConsumoCdnCard = () => (
    <TrafficChartCard
      key="consumo-cdn"
      title="Consumo de CDN"
      icon={DnsOutlined}
      loading={loadingDashFlow?.consumoCdn}
      color={{ graph: corPadraoCatalogo, icon: corPadraoCatalogo }}
      data={dataGraph?.consumoCdn}
      percentil={valuePercentil?.consumoCdn}
      handleAplicarZoom={handleAplicarZoom}
      modalColor="blue"
    />
  );

  // "Divisão por Classes" hospeda o card "Consumo de CDN", então a seção
  // aparece também quando não há classes mas há CDN habilitado.
  const totalCardsClasses =
    classeItensVisiveis?.length + (cdnItensVisiveis?.length > 0 ? 1 : 0);
  const mostrarSecaoClasses =
    classeItensVisiveis?.length > 0 || cdnItensVisiveis?.length > 0;
  const mostrarSecaoCdn = cdnItensVisiveis?.length > 0;

  function normalizeStackedData(data = []) {
    if (!Array.isArray(data) || data?.length === 0) return data;

    return data?.map((row) => {
      const { clock, Outros, ...rest } = row;

      return {
        clock,
        ...(Outros !== undefined ? { Outros } : {}),
        ...rest,
      };
    });
  }

  const optionsTimeEasyFlow = {
    0: {
      value: dayjs()
        .subtract(5, "minute")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimos 5 minutos",
    },
    1: {
      value: dayjs()
        .subtract(15, "minute")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimos 15 minutos",
    },
    2: {
      value: dayjs()
        .subtract(30, "minute")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimos 30 minutos",
    },
    3: {
      value: dayjs()
        .subtract(1, "hour")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Última hora",
    },
    4: {
      value: dayjs()
        .subtract(3, "hour")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimas 3 horas",
    },
    5: {
      value: dayjs()
        .subtract(6, "hour")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimas 6 horas",
    },
    6: {
      value: dayjs()
        .subtract(12, "hour")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimas 12 horas",
    },
    7: {
      value: dayjs()
        .subtract(24, "hour")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimas 24 horas",
    },
    8: {
      value: dayjs()
        .subtract(2, "day")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimos 2 dias",
    },
    9: {
      value: dayjs()
        .subtract(7, "day")
        .set("second", 0)
        .set("millisecond", 0)
        .toDate(),
      label: "Últimos 7 dias",
    },
  };
  useEffect(() => {
    setPropsLayout((prev) => ({
      ...prev,
      optionsTime: optionsTimeEasyFlow,
      rangeDays: 7,
    }));

    return () => {
      setPropsLayout({});
    };
  }, []);

  const clearData = () => {
    setItensHabilitadosIds(null);
    setRows([]);
    setDataGraph({ trafficAns: [] });
    setValuePercentil({ trafficAns: 0 });
  };

  useEffect(() => {
    if (clienteId && filterData?.initialDate && filterData?.finalDate) {
      //Garantir que a funcao será chamada com range correto
      //Pois outras paginas de dashboard tem limite maior que 7 dias
      if (
        dayjs(filterData.finalDate).isSameOrAfter(filterData.initialDate) &&
        dayjs(filterData.finalDate).diff(filterData.initialDate, "day", true) <=
          7
      ) {
        fetchAllData();
      }
    } else {
      clearData();
    }
  }, [clienteId, filterData?.initialDate, filterData?.finalDate]);

  const formatBandwidth = (value) => {
    if (value == null) return "-";

    const gbps = value / 1_000_000_000;
    if (gbps >= 1) {
      return `${gbps.toFixed(2)} Gb/s`;
    }

    const mbps = value / 1_000_000;
    return `${mbps.toFixed(2)} Mb/s`;
  };

  const getTrafegoEasyFlow = async (dateInitial, finalDate, signal) => {
    setLoadingDashFlow((prev) => ({ ...prev, trafficAns: true }));
    try {
      const response = await api.post(
        "/cliente/trafficEasyFlow",
        {
          clienteId,
          dateInitial: dateInitial,
          finalDate: finalDate,
          topItens: topItens,
        },
        { signal },
      );

      setDataGraph((prev) => ({
        ...prev,
        trafficAns: normalizeStackedData(response?.data?.traffic) || [],
      }));
      setRows(response?.data?.rows || []);
      setValuePercentil((prev) => ({
        ...prev,
        trafficAns: response?.data?.percentil || 0,
      }));
    } catch (error) {
      console.error("Erro ao buscar dados de tráfego do EasyFlow:", error);
    } finally {
      if (!signal.aborted) {
        setLoadingDashFlow((prev) => ({ ...prev, trafficAns: false }));
      }
    }
  };

  // Busca o tráfego de um único CDN habilitado — "item" vem do catálogo
  // (ItensFlow), e item.id é usado como chave de dataGraph/loadingDashFlow
  const getTrafegoEasyFlowServico = async (
    item,
    dateInitial,
    finalDate,
    signal,
  ) => {
    setLoadingDashFlow((prev) => ({ ...prev, [item.id]: true }));
    try {
      const response = await api.post(
        "/cliente/trafficEasyFlowCdn",
        {
          cdn: item.identificador,
          clienteId,
          dateInitial: dateInitial,
          finalDate: finalDate,
        },
        { signal },
      );
      setDataGraph((prev) => ({
        ...prev,
        [item.id]: response?.data?.traffic || [],
      }));
      setValuePercentil((prev) => ({
        ...prev,
        [item.id]: response?.data?.percentil || 0,
      }));
    } catch (error) {
      console.error("Erro ao buscar dados de tráfego do EasyFlow:", error);
    } finally {
      if (!signal.aborted) {
        setLoadingDashFlow((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  // Busca o tráfego de uma única classe habilitada — mesma lógica de
  // getTrafegoEasyFlowServico (uma chamada por item, loading próprio via
  // item.id, independente das demais)
  const getTrafegoEasyFlowClasse = async (
    item,
    dateInitial,
    finalDate,
    signal,
  ) => {
    setLoadingDashFlow((prev) => ({ ...prev, [item.id]: true }));
    try {
      const response = await api.post(
        "/cliente/trafficEasyFlowClasses",
        {
          classe: item.identificador,
          clienteId,
          dateInitial: dateInitial,
          finalDate: finalDate,
        },
        { signal },
      );
      setDataGraph((prev) => ({
        ...prev,
        [item.id]: response?.data?.traffic || [],
      }));
      setValuePercentil((prev) => ({
        ...prev,
        [item.id]: response?.data?.percentil || 0,
      }));
    } catch (error) {
      console.error(
        "Erro ao buscar dados de tráfego por classe do EasyFlow:",
        error,
      );
    } finally {
      if (!signal.aborted) {
        setLoadingDashFlow((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  // Soma o tráfego de todos os CDNs habilitados para o cliente ("Consumo de
  // CDN") — loading/dados próprios via a chave fixa "consumoCdn"
  const getConsumoCdn = async (dateInitial, finalDate, signal) => {
    setLoadingDashFlow((prev) => ({ ...prev, consumoCdn: true }));
    try {
      const response = await api.post(
        "/cliente/trafficEasyFlowCdnConsumo",
        {
          clienteId,
          dateInitial: dateInitial,
          finalDate: finalDate,
        },
        { signal },
      );
      setDataGraph((prev) => ({
        ...prev,
        consumoCdn: response?.data?.traffic || [],
      }));
      setValuePercentil((prev) => ({
        ...prev,
        consumoCdn: response?.data?.percentil || 0,
      }));
    } catch (error) {
      console.error("Erro ao buscar consumo de CDN do EasyFlow:", error);
    } finally {
      if (!signal.aborted) {
        setLoadingDashFlow((prev) => ({ ...prev, consumoCdn: false }));
      }
    }
  };

  // Catálogo completo do Flow (ItensFlow) — retorna os itens já filtrados
  // por tipo (CDN/Tráfego) para uso imediato em fetchAllData
  const fetchCatalogoFlow = async (signal) => {
    try {
      const response = await api.get("/cliente/find/itensFlow", { signal });
      const itens = (response?.data || []).filter(
        (item) => item.tipo === TIPO_CDN || item.tipo === TIPO_CLASSE,
      );
      setCatalogoFlow(itens);
      return itens;
    } catch (error) {
      console.error("Erro ao buscar catálogo do Flow:", error);
      return [];
    }
  };

  // Quais itens do Flow (CDNs/classes) estão habilitados para o cliente
  // logado — retorna o Set de ids já pronto para uso imediato em fetchAllData
  const fetchConfigVisibilidadeCliente = async (signal) => {
    try {
      const response = await api.post(
        "/cliente/find/configItensFlowCliente",
        { clienteId },
        { signal },
      );
      const habilitadosIds = new Set(
        (response?.data || []).map((item) => item.itenFlowId),
      );
      setItensHabilitadosIds(habilitadosIds);
      return habilitadosIds;
    } catch (error) {
      console.error(
        "Erro ao buscar configuração de visibilidade do Flow:",
        error,
      );
      return new Set();
    }
  };

  const fetchAllData = async () => {
    setCounter(null);
    stopCronometro();
    setLoadingCronometro(true);

    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;
    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime[selectTime]?.value;
      finalDate = dayjs().set("second", 0).set("millisecond", 0).toDate();
    }
    if (controllerDashboard) {
      controllerDashboard.abort();
    }
    const newController = new AbortController();
    const signal = newController.signal;
    setControllerDashboard(newController);

    // O catálogo e a habilitação por cliente precisam estar resolvidos antes
    // de decidir quais CDNs/classes buscar — só chamamos a API para o que
    // realmente está configurado e habilitado para este cliente.
    const [catalogo, habilitadosIds] = await Promise.all([
      fetchCatalogoFlow(signal),
      fetchConfigVisibilidadeCliente(signal),
    ]);

    const itensHabilitados = catalogo.filter((item) =>
      habilitadosIds.has(item.id),
    );
    const cdnsHabilitados = itensHabilitados.filter(
      (item) => item.tipo === TIPO_CDN,
    );
    const classesHabilitadas = itensHabilitados.filter(
      (item) => item.tipo === TIPO_CLASSE,
    );

    await Promise.all([
      getTrafegoEasyFlow(dateInitial, finalDate, signal),
      ...cdnsHabilitados.map((item) =>
        getTrafegoEasyFlowServico(item, dateInitial, finalDate, signal),
      ),
      ...classesHabilitadas.map((item) =>
        getTrafegoEasyFlowClasse(item, dateInitial, finalDate, signal),
      ),
      ...(cdnsHabilitados.length > 0
        ? [getConsumoCdn(dateInitial, finalDate, signal)]
        : []),
    ]);
    if (!signal.aborted) {
      setLoadingCronometro(false);
      startCronometro();
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
    },
    // Usuário de grupo econômico: blocos consolidados chegam com o membro
    // dono (ownerNome) — coluna exibida somente nesse caso
    ...(rows?.some((row) => !!row?.ownerNome)
      ? [
          {
            field: "ownerNome",
            headerName: "Empresa",
            flex: 1,
            renderCell: (params) =>
              params?.row?.ownerNome ? (
                <GrupoChip variant="owner" label={params?.row?.ownerNome} />
              ) : (
                ""
              ),
          },
        ]
      : []),
    {
      field: "max",
      headerName: "Max",
      flex: 0.6,
      align: "right",
      headerAlign: "right",
      valueFormatter: (params) => {
        return formatBandwidth(params);
      },
      renderCell: (params) => {
        return <Tooltip>{formatBandwidth(params.value)}</Tooltip>;
      },
    },
    {
      field: "mean",
      headerName: "Mean",
      flex: 0.6,
      align: "right",
      headerAlign: "right",
      valueFormatter: (params) => formatBandwidth(params),
    },
    {
      field: "last",
      headerName: "Last",
      flex: 0.6,
      align: "right",
      headerAlign: "right",
      valueFormatter: (params) => formatBandwidth(params),
    },
  ];

  const DefaultToolTip = ({ active, payload, label, maxItems = topItens }) => {
    // Se o tooltip não estiver ativo ou não tiver dados, não renderiza nada
    if (!active || !payload || payload?.length === 0) return null;

    /**
     * ===== CONFIGURAÇÕES PADRÃO =====
     */

    // Cor padrão usada caso a série não forneça uma cor
    const DEFAULT_COLOR = theme.palette.primary.main;

    // Timezone padrão (UTC caso não queira conversão)
    const TIMEZONE = "America/Sao_Paulo";

    // Função simples para formatar bytes (substitui formatBytes externo)
    const formatBytes = (value = 0) => {
      if (value === 0) return "0 bps";
      const units = ["bps", "Kbps", "Mbps", "Gbps", "Tbps"];
      const i = Math.floor(Math.log(value) / Math.log(1000));
      return `${(value / Math.pow(1000, i)).toFixed(2)} ${units[i]}`;
    };

    /**
     * ===== ORDENAÇÃO DAS SÉRIES =====
     */
    const orderedPayload = [...payload];
    /**
     * ===== CONTROLE DE ITENS VISÍVEIS =====
     */

    // Itens que serão exibidos individualmente no tooltip
    // Ex: maxItems = 3 → mostra 2 itens + "Outros"
    const visibleItems = [...orderedPayload]
      ?.reverse()
      ?.slice(0, maxItems < orderedPayload?.length ? maxItems - 1 : maxItems);

    // Itens que serão agrupados em "Outros"
    const otherItems = [...orderedPayload]
      ?.reverse()
      ?.slice(maxItems < orderedPayload?.length ? maxItems - 1 : maxItems);

    // Soma dos valores agrupados em "Outros"
    const otherSum = otherItems?.reduce(
      (sum, item) => sum + (item?.value || 0),
      0,
    );

    /**
     * ===== RENDERIZAÇÃO =====
     */

    return (
      <Box sx={styles.containerTooltip}>
        {/* Cabeçalho com data */}
        <Typography fontSize="14px" fontWeight={500} mb={1}>
          {`Data: ${dayjs.unix(label).tz(TIMEZONE).format("DD/MM/YY HH:mm")}`}
        </Typography>

        {/* Itens visíveis individualmente */}
        {visibleItems?.map((item) => (
          <Stack
            key={item.dataKey}
            direction="row"
            alignItems="center"
            gap={0.5}
          >
            {/* Quadrado de cor da série */}
            <Box
              sx={{
                bgcolor: item?.color || DEFAULT_COLOR,
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                border: `1px solid ${item?.color || DEFAULT_COLOR}`,
              }}
            />

            {/* Nome da série + valor */}
            <Box sx={styles.boxInfoTooltip}>
              <Typography fontSize="14px">{`${item.dataKey}:`}</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                {formatBytes(item.value)}
              </Typography>
            </Box>
          </Stack>
        ))}

        {/* Agrupamento "Outros" */}
        {otherItems?.length > 0 && (
          <Stack direction="row" alignItems="center" mt={0.5}>
            <Box
              sx={{
                bgcolor: DEFAULT_COLOR,
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                border: `1px solid ${DEFAULT_COLOR}`,
              }}
            />
            <Box sx={styles.boxInfoTooltip}>
              <Typography fontSize="14px">{`Outros: `}</Typography>
              <Typography fontSize="14px" fontWeight={"bold"}>
                {` ${formatBytes(otherSum)}`}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Total geral */}
        {orderedPayload?.length > 1 && (
          <Typography fontSize="14px" mt={1} fontWeight={500}>
            {`Total: ${formatBytes(
              orderedPayload?.reduce(
                (sum, item) => sum + (item?.value || 0),
                0,
              ),
            )}`}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <Grid
        container
        spacing={1}
        columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
      >
        <Grid item xs={2} sm={4} md={6} lg={6} xl={6}>
          <Paper
            sx={styles.paperContainer}
            elevation={theme.palette.mode === "dark" ? 0 : 3}
          >
            <Box sx={styles.linearProgress}>
              <Box sx={styles.boxLoading}>
                {loadingDashFlow?.trafficAns && <LinearProgress />}
              </Box>
            </Box>

            <Box sx={styles.headerRow}>
              <Box sx={styles.boxTitle}>
                <Box
                  sx={{
                    ...styles.containerIconCard,
                    backgroundColor: colorGraph?.asn.title,
                  }}
                >
                  <EasyFlowIcon sx={styles.icon} />
                </Box>
                <Typography
                  sx={{
                    ...styles.cardSubText,
                    color: colorGraph?.asn.title,
                  }}
                >
                  Trafégo por ASN
                </Typography>
              </Box>
              <Tooltip title="Abrir histórico ampliado">
                <IconButton
                  size="small"
                  onClick={() => setOpenAsnModal(true)}
                  sx={styles.historyButton}
                >
                  <OpenInFullOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={styles.boxGraph}>
              <LineChartComponent
                data={dataGraph?.trafficAns}
                isZoomEnabled={true}
                onZoomChange={(range) => {
                  // Use .unix() para interpretar o valor como segundos
                  const startDate = dayjs.unix(range.start).tz(timeZone);
                  const endDate = dayjs.unix(range.end).tz(timeZone);

                  handleAplicarZoom(startDate, endDate);
                }}
                maxValuebps={100}
                maxTicks={10}
                mainColor={colorGraph?.asn.graph}
                mainLineColor={colorGraph?.asn.graph}
                customTooltip={DefaultToolTip}
                xOptions={{
                  tickFormatter: (clock) =>
                    dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
                }}
                referenceLines={[
                  {
                    value: valuePercentil?.trafficAns,
                    opt: {
                      stroke: "red",
                      label: {
                        value: "95th",
                        fill: theme.palette.mode === "dark" ? "white" : "black",
                        fontSize: "12px",
                        fontWeight: "bold",
                        position: "insideRight",
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                ]}
              />
            </Box>
            <Paper variant="outlined" elevetion={0} sx={styles.datagridPaper}>
              <DatagridComponent rows={rows} columns={columns} />
            </Paper>
          </Paper>
          <ModalChartHistory
            open={openAsnModal}
            onClose={() => setOpenAsnModal(false)}
            title="Trafégo por ASN"
            icon={EasyFlowIcon}
            color="green"
            unit="bps"
            initialData={dataGraph?.trafficAns}
            maxValuebps={100}
            minValuebps={0}
          />
        </Grid>

        {itensHabilitadosIds && clienteId && itensFlowVisiveis?.length === 0 ? (
          <Grid item xs={2} sm={4} md={6} lg={6} xl={6}>
            <Box sx={styles.emptyVisibilidade}>
              <Typography sx={styles.emptyVisibilidadeText}>
                Nenhum CDN ou tipo de tráfego habilitado para o seu contrato.
                Entre em contato com o suporte para mais informações.
              </Typography>
            </Box>
          </Grid>
        ) : (
          <Grid item xs={2} sm={4} md={6} lg={6} xl={6}>
            <Box sx={styles.secoesContainer}>
              {mostrarSecaoClasses && (
                <Accordion
                  elevation={theme.palette.mode === "dark" ? 0 : 3}
                  defaultExpanded
                  sx={styles.accordionSecao}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreOutlined />}
                    sx={styles.accordionSummary}
                  >
                    <Box sx={styles.accordionHeaderRow}>
                      <Box sx={styles.accordionTitleRow}>
                        <CategoryOutlined
                          fontSize="small"
                          sx={styles.accordionIcon}
                        />
                        <Typography sx={styles.accordionTitulo}>
                          Divisão por Classes
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={totalCardsClasses}
                        sx={styles.accordionCountChip}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={styles.accordionDetails}>
                    <Box
                      sx={{
                        ...styles.cardsGrid,
                        gridTemplateColumns: getColunasCards(totalCardsClasses),
                      }}
                    >
                      {classeItensVisiveis?.map(renderTrafficCard)}
                      {mostrarSecaoCdn && renderConsumoCdnCard()}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {mostrarSecaoClasses && mostrarSecaoCdn && (
                <Divider sx={styles.divisorSecoes} />
              )}

              {mostrarSecaoCdn && (
                <Accordion
                  elevation={theme.palette.mode === "dark" ? 0 : 3}
                  defaultExpanded
                  sx={styles.accordionSecao}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreOutlined />}
                    sx={styles.accordionSummary}
                  >
                    <Box sx={styles.accordionHeaderRow}>
                      <Box sx={styles.accordionTitleRow}>
                        <DnsOutlined
                          fontSize="small"
                          sx={styles.accordionIcon}
                        />
                        <Typography sx={styles.accordionTitulo}>
                          Divisão por CDN
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={cdnItensVisiveis?.length}
                        sx={styles.accordionCountChip}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={styles.accordionDetails}>
                    <Box
                      sx={{
                        ...styles.cardsGrid,
                        gridTemplateColumns: getColunasCards(
                          cdnItensVisiveis?.length,
                        ),
                      }}
                    >
                      {cdnItensVisiveis?.map(renderTrafficCard)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
}
